import React, { ReactNode, useState } from 'react';
import {
    ChatArtifact,
    Citation,
    ChartArtifact,
    GeneratedArtifact as GeneratedArtifactValue,
    RichArtifact,
    TableArtifact,
    ToolActivity,
} from '../chat-types';
import GeneratedArtifact from './GeneratedArtifact';

type MessageBlock =
    | { type: 'heading'; level: number; content: string }
    | { type: 'paragraph'; content: string }
    | { type: 'list'; ordered: boolean; items: string[] }
    | { type: 'code'; content: string }
    | { type: 'table'; artifact: TableArtifact }
    | { type: 'chart'; artifact: ChartArtifact };

type RichMessageProps = {
    text: string;
    isStreaming: boolean;
    tools: ToolActivity[] | undefined;
    citations: Citation[] | undefined;
    artifacts: ChatArtifact[] | undefined;
};

const SPECIAL_LINE = /^(#{1,3}\s|[-*]\s|\d+\.\s|```|\|)/;
const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g;

function normalizeMarkdownBoundaries(text: string): string {
    // A streamed heading can arrive in a separate token without its intended newline.
    return text.replace(/(\S)(?=#{2,3}\s+\S)/g, '$1\n\n');
}

function parseTable(lines: string[]): TableArtifact | null {
    if (lines.length < 2) {
        return null;
    }

    const splitRow = (line: string) =>
        line
            .trim()
            .replace(/^\||\|$/g, '')
            .split('|')
            .map((cell) => cell.trim());

    const separatorCells = splitRow(lines[1]);
    if (
        separatorCells.length === 0 ||
        !separatorCells.every((cell) => /^:?-{3,}:?$/.test(cell))
    ) {
        return null;
    }

    const columns = splitRow(lines[0]);
    const rows = lines.slice(2).map(splitRow);
    return columns.length > 0 && rows.length > 0 ? { type: 'table', columns, rows } : null;
}

function parseChart(content: string): ChartArtifact | null {
    try {
        const value = JSON.parse(content) as Partial<ChartArtifact>;
        if (
            Array.isArray(value.data) &&
            value.data.every(
                (datum) =>
                    typeof datum?.label === 'string' &&
                    typeof datum?.value === 'number' &&
                    Number.isFinite(datum.value),
            )
        ) {
            return {
                type: 'chart',
                chartType: value.chartType === 'line' ? 'line' : 'bar',
                title: typeof value.title === 'string' ? value.title : undefined,
                data: value.data,
            };
        }
    } catch {
        return null;
    }
    return null;
}

function parseBlocks(text: string): MessageBlock[] {
    const lines = text.replace(/\r/g, '').split('\n');
    const blocks: MessageBlock[] = [];
    let cursor = 0;

    while (cursor < lines.length) {
        const line = lines[cursor];

        if (!line.trim()) {
            cursor += 1;
        } else if (line.startsWith('```')) {
            const language = line.slice(3).trim().toLowerCase();
            const codeLines: string[] = [];
            cursor += 1;
            while (cursor < lines.length && !lines[cursor].startsWith('```')) {
                codeLines.push(lines[cursor]);
                cursor += 1;
            }
            cursor += cursor < lines.length ? 1 : 0;
            const content = codeLines.join('\n');
            const chart = language === 'chart' ? parseChart(content) : null;
            blocks.push(chart ? { type: 'chart', artifact: chart } : { type: 'code', content });
        } else if (/^#{1,3}\s/.test(line)) {
            const marker = line.match(/^#+/)?.[0] ?? '#';
            blocks.push({
                type: 'heading',
                level: marker.length,
                content: line.slice(marker.length).trim(),
            });
            cursor += 1;
        } else if (/^[-*]\s/.test(line) || /^\d+\.\s/.test(line)) {
            const ordered = /^\d+\.\s/.test(line);
            const items: string[] = [];
            const itemPattern = ordered ? /^\d+\.\s+/ : /^[-*]\s+/;
            while (cursor < lines.length && itemPattern.test(lines[cursor])) {
                items.push(lines[cursor].replace(itemPattern, '').trim());
                cursor += 1;
            }
            blocks.push({ type: 'list', ordered, items });
        } else if (line.includes('|') && cursor + 1 < lines.length) {
            const tableLines = [line];
            let tableCursor = cursor + 1;
            while (tableCursor < lines.length && lines[tableCursor].includes('|')) {
                tableLines.push(lines[tableCursor]);
                tableCursor += 1;
            }
            const table = parseTable(tableLines);
            if (table) {
                blocks.push({ type: 'table', artifact: table });
                cursor = tableCursor;
            } else {
                blocks.push({ type: 'paragraph', content: line });
                cursor += 1;
            }
        } else {
            const paragraphLines = [line];
            cursor += 1;
            while (
                cursor < lines.length &&
                lines[cursor].trim() &&
                !SPECIAL_LINE.test(lines[cursor])
            ) {
                paragraphLines.push(lines[cursor]);
                cursor += 1;
            }
            blocks.push({ type: 'paragraph', content: paragraphLines.join(' ') });
        }
    }

    return blocks;
}

function renderInline(text: string): ReactNode[] {
    const nodes: ReactNode[] = [];
    let previousEnd = 0;
    let match = INLINE_PATTERN.exec(text);

    while (match) {
        if (match.index > previousEnd) {
            nodes.push(
                <React.Fragment key={`text-${previousEnd}`}>
                    {text.slice(previousEnd, match.index)}
                </React.Fragment>,
            );
        }

        const token = match[0];
        if (token.startsWith('**')) {
            nodes.push(<strong key={`strong-${match.index}`}>{token.slice(2, -2)}</strong>);
        } else if (token.startsWith('`')) {
            nodes.push(<code key={`code-${match.index}`}>{token.slice(1, -1)}</code>);
        } else {
            const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
            if (linkMatch) {
                nodes.push(
                    <a
                        key={`link-${match.index}`}
                        href={linkMatch[2]}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {linkMatch[1]}
                    </a>,
                );
            }
        }

        previousEnd = match.index + token.length;
        match = INLINE_PATTERN.exec(text);
    }

    if (previousEnd < text.length) {
        nodes.push(
            <React.Fragment key={`text-${previousEnd}`}>{text.slice(previousEnd)}</React.Fragment>,
        );
    }

    INLINE_PATTERN.lastIndex = 0;
    return nodes;
}

function downloadTable(artifact: TableArtifact) {
    const escapeCell = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`;
    const csv = [
        artifact.columns.map(escapeCell).join(','),
        ...artifact.rows.map((row) => row.map(escapeCell).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${artifact.title || 'rtb-results'}.csv`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
    link.click();
    URL.revokeObjectURL(url);
}

function DataTable({ artifact }: { artifact: TableArtifact }) {
    const rowOccurrences = new Map<string, number>();
    const keyedRows = artifact.rows.map((row) => {
        const baseKey = row.map(String).join('|');
        const occurrence = (rowOccurrences.get(baseKey) ?? 0) + 1;
        rowOccurrences.set(baseKey, occurrence);
        return { key: `${baseKey}-${occurrence}`, row };
    });

    return (
        <section className="artifact-card table-artifact">
            <div className="artifact-heading">
                <div>
                    <span className="artifact-kicker">Table</span>
                    {artifact.title && <h4>{artifact.title}</h4>}
                </div>
                <button type="button" className="text-button" onClick={() => downloadTable(artifact)}>
                    Download CSV
                </button>
            </div>
            <div className="table-scroll">
                <table>
                    <thead>
                        <tr>
                            {artifact.columns.map((column) => (
                                <th key={column} scope="col">
                                    {column}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {keyedRows.map(({ key, row }) => (
                            <tr key={key}>
                                {row.map((cell, cellIndex) => (
                                    <td key={`${artifact.columns[cellIndex] || 'cell'}-${String(cell)}`}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function DataChart({ artifact }: { artifact: ChartArtifact }) {
    const width = 680;
    const height = 280;
    const padding = 42;
    const maxValue = Math.max(...artifact.data.map((datum) => datum.value), 1);
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;
    const step = usableWidth / Math.max(artifact.data.length, 1);
    const points = artifact.data
        .map((datum, position) => {
            const x = padding + step * position + step / 2;
            const y = height - padding - (datum.value / maxValue) * usableHeight;
            return `${x},${y}`;
        })
        .join(' ');

    return (
        <section className="artifact-card chart-artifact">
            <div className="artifact-heading">
                <div>
                    <span className="artifact-kicker">Visualization</span>
                    <h4>{artifact.title || 'RTB results'}</h4>
                </div>
                <span className="chart-type">{artifact.chartType === 'line' ? 'Line' : 'Bar'}</span>
            </div>
            <div className="chart-wrap" role="img" aria-label={artifact.title || 'RTB data chart'}>
                <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
                    <line
                        className="chart-axis"
                        x1={padding}
                        y1={height - padding}
                        x2={width - padding}
                        y2={height - padding}
                    />
                    {artifact.chartType === 'line' ? (
                        <>
                            <polyline className="chart-line" points={points} />
                            {artifact.data.map((datum, position) => {
                                const x = padding + step * position + step / 2;
                                const y =
                                    height -
                                    padding -
                                    (datum.value / maxValue) * usableHeight;
                                return (
                                    <g key={datum.label} className="chart-point-group">
                                        <circle className="chart-point" cx={x} cy={y} r="6" />
                                        <text className="chart-value" x={x} y={y - 14}>
                                            {datum.value}
                                        </text>
                                        <text
                                            className="chart-label"
                                            x={x}
                                            y={height - padding + 24}
                                        >
                                            {datum.label}
                                        </text>
                                    </g>
                                );
                            })}
                        </>
                    ) : (
                        artifact.data.map((datum, position) => {
                            const barWidth = Math.min(step * 0.56, 64);
                            const barHeight = (datum.value / maxValue) * usableHeight;
                            const x = padding + step * position + (step - barWidth) / 2;
                            const y = height - padding - barHeight;
                            return (
                                <g key={datum.label} className="chart-bar-group">
                                    <rect
                                        className="chart-bar"
                                        x={x}
                                        y={y}
                                        width={barWidth}
                                        height={barHeight}
                                        rx="8"
                                    />
                                    <text
                                        className="chart-value"
                                        x={x + barWidth / 2}
                                        y={y - 10}
                                    >
                                        {datum.value}
                                    </text>
                                    <text
                                        className="chart-label"
                                        x={x + barWidth / 2}
                                        y={height - padding + 24}
                                    >
                                        {datum.label}
                                    </text>
                                </g>
                            );
                        })
                    )}
                </svg>
            </div>
        </section>
    );
}

function ToolTrace({ tools }: { tools: ToolActivity[] }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <section className="tool-trace">
            <button type="button" onClick={() => setExpanded((current) => !current)}>
                <span className="tool-status-dot" />
                {tools.length} RTB {tools.length === 1 ? 'tool' : 'tools'} used
                <span aria-hidden="true">{expanded ? '−' : '+'}</span>
            </button>
            {expanded && (
                <ul>
                    {tools.map((tool) => (
                        <li key={`${tool.name}-${tool.detail || ''}`}>
                            <span>{tool.name}</span>
                            {tool.detail && <small>{tool.detail}</small>}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function isGeneratedArtifact(artifact: ChatArtifact): artifact is GeneratedArtifactValue {
    return 'artifactType' in artifact;
}

function isRichArtifact(artifact: ChatArtifact): artifact is RichArtifact {
    return 'type' in artifact;
}

export default function RichMessage({
    text,
    isStreaming = false,
    tools = [],
    citations = [],
    artifacts = [],
}: RichMessageProps) {
    const [copied, setCopied] = useState(false);
    const normalizedText = normalizeMarkdownBoundaries(text);
    const blocks = parseBlocks(normalizedText);

    async function copyResponse() {
        await navigator.clipboard.writeText(normalizedText);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
    }

    return (
        <div className="rich-message">
            <div className="message-content">
                {blocks.map((block) => {
                    let blockKey: string;
                    if (block.type === 'table' || block.type === 'chart') {
                        blockKey = JSON.stringify(block.artifact);
                    } else if (block.type === 'list') {
                        blockKey = `${block.type}-${block.items.join('|')}`;
                    } else {
                        blockKey = `${block.type}-${block.content}`;
                    }
                    if (block.type === 'heading') {
                        return (
                            <h3 key={blockKey} className={`response-heading level-${block.level}`}>
                                {renderInline(block.content)}
                            </h3>
                        );
                    }
                    if (block.type === 'paragraph') {
                        return <p key={blockKey}>{renderInline(block.content)}</p>;
                    }
                    if (block.type === 'list') {
                        const List = block.ordered ? 'ol' : 'ul';
                        return (
                            <List key={`${block.type}-${block.items.join('|')}`}>
                                {block.items.map((item) => (
                                    <li key={item}>{renderInline(item)}</li>
                                ))}
                            </List>
                        );
                    }
                    if (block.type === 'code') {
                        return (
                            <pre key={blockKey}>
                                <code>{block.content}</code>
                            </pre>
                        );
                    }
                    if (block.type === 'table') {
                        return <DataTable key={blockKey} artifact={block.artifact} />;
                    }
                    return <DataChart key={blockKey} artifact={block.artifact} />;
                })}
                {isStreaming && <span className="streaming-cursor" aria-label="Streaming response" />}
            </div>

            {artifacts.filter(isRichArtifact).map((artifact) =>
                artifact.type === 'table' ? (
                    <DataTable
                        key={`table-${artifact.title || JSON.stringify(artifact.columns)}`}
                        artifact={artifact}
                    />
                ) : (
                    <DataChart
                        key={`chart-${artifact.title || JSON.stringify(artifact.data)}`}
                        artifact={artifact}
                    />
                ),
            )}

            {artifacts.filter(isGeneratedArtifact).map((artifact) => (
                <GeneratedArtifact
                    key={JSON.stringify(artifact)}
                    artifact={artifact}
                />
            ))}

            {tools.length > 0 && <ToolTrace tools={tools} />}

            {citations.length > 0 && (
                <section className="citations" aria-label="Sources">
                    <span>Sources</span>
                    <div>
                        {citations.map((citation) =>
                            citation.url ? (
                                <a
                                    key={`${citation.label}-${citation.url}`}
                                    href={citation.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {citation.label}
                                </a>
                            ) : (
                                <span key={`${citation.label}-${citation.detail || ''}`}>
                                    {citation.label}
                                </span>
                            ),
                        )}
                    </div>
                </section>
            )}

            {!isStreaming && text && (
                <div className="message-actions">
                    <button type="button" onClick={copyResponse}>
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            )}
        </div>
    );
}
